/**
 * @file services/firms.js
 * @since v1.0
 * @author Noel Flores <noel.flores@struck.com>
 */

// #region dependencies
// #endregion
// #region common
import { ExtendedCompanyInfo } from 'api';
import type { IntelligenceExcelExport } from 'api/models/IntelligenceExcelExport';
import { FIRMS, MEASUREMENT_DATES } from 'common/endpoints';
import { Firm, FirmCreateDocRequestPayload, FirmCreateQuestionRequestPayload } from 'common/types/firm';
import { ParsedFundByMeasurementDate } from 'common/types/store';
import BaseServiceRetryWrapper from './baseRetry';
import { PayloadToUpdateRequestedQuestion } from './hooks/useRequestDocsAndQuestions/types';
// #endregion

class FirmsService extends BaseServiceRetryWrapper {
  /**
   * @function
   * @name allData
   * @description load firm by name selected.
   */
  allData = () => this.get(FIRMS.all);

  /**
   * @function
   * @name getUserManagementFirmList
   * @description get firms filtered for user management view
   */
  getUserManagementFirmList = (userId: string) => this.get(FIRMS.userManagementFirmList(userId));

  /**
   * @function
   * @name getSelectedUserPermissions
   * @description get firm permissions for the selected user
   */
  getSelectedUserPermissions = (userId: string) => this.get(FIRMS.selectedUserPermissions(userId));

  /**
   * @function
   * @name getFirmById
   * @description Get firm by its id
   * @param {integer} firmId the firm id
   * @return {object}
   */
  getFirmById = (firmId: number) => this.get(FIRMS.firmById(firmId));

  /**
   * @function
   * @name getFundByFirmId
   * @description Get the list of funds with its companies (summary) by firm id
   * @param {integer} firmId
   * @return {array}
   */
  getFundByFirmId = (firmId: string): Promise<any> => this.get(FIRMS.getFundByFirmId(firmId));

  /**
   * @function
   * @name getFundListByFirmId
   * @description Get the list of funds by firm id that will be shown in navigation
   * @param {integer} firmId
   * @return {array}
   */
  getFundListByFirmId = (firmId: number): Promise<any> => this.get(FIRMS.getFundListByFirmId(firmId));

  /**
   * @function
   * @name createFirm
   * @description Create new firm
   * @param {integer} Firm data
   */
  createFirm = (data: Firm): Promise<any> => this.post(FIRMS.create, data);

  updateFirmById = (firmId: number, data: Firm): Promise<any> => this.put(FIRMS.firmById(firmId), data);

  getMeasurementDatesByFirmId = (id: number): Promise<any> => this.get(MEASUREMENT_DATES.measurementDatesByFirmId(id));

  createFirmCompany = (data: any): Promise<any> => this.post(FIRMS.createFirmCompany, data);

  getCompaniesFromPreviousMD = (firmId: string, date: string) => {
    const endpoint = FIRMS.getCompaniesFromPreviousMD(firmId, date);
    return this.get(endpoint);
  };

  addUserToFirm = (firmId: number, userData: unknown) => this.put(FIRMS.addUserToFirm(firmId), userData);

  removeUserFromFirm = (firmId: number, userId: number) => this.delete(FIRMS.removeUserFromFirm(firmId, userId));

  getFirmCompanyExtendedInfo = (firmId: number, companyId: number): Promise<ExtendedCompanyInfo> =>
    this.get(FIRMS.getFirmCompanyExtendedInfo(firmId, companyId)) as Promise<ExtendedCompanyInfo>;

  createOrUpdateCompGroups = (firmId: number, data: any): Promise<any> =>
    this.post(FIRMS.createOrUpdateCompGroups(firmId), data);

  getPendingDecisionsByFirm = (firmId: string) => this.get(FIRMS.getPendingDecisions(firmId));

  updatePendingDecisionsByFirm = (firmId: string, decisionData: unknown) =>
    this.post(FIRMS.getPendingDecisions(firmId), decisionData);

  getTasksProgress = (firmId: string) => this.get(FIRMS.getTasksProgress(firmId));

  getFirmSetOfQuestionsAndDocuments = (firmId: string): Promise<any> =>
    this.get(FIRMS.getFirmSetOfQuestionsAndDocuments(firmId));

  createRequestedTasks = (data: any): Promise<any> => this.post(FIRMS.createRequestedTasks, data);

  getTaskProgressDetail = (taskId: string) => this.get(FIRMS.getTaskProgressDetail(taskId));

  changeUserFirmRole = (firmId: number, userId: number, roleId: number) =>
    this.put(FIRMS.changeUserFirmRole(firmId, userId), { role_id: roleId });

  getAllMeasurementDatesByFirmIdGroupedByFund = (id: string): Promise<any> =>
    this.get(MEASUREMENT_DATES.allMeasurementDatesByFirmIdGroupedByFund(id));

  createRequestedQuestion = (data: FirmCreateQuestionRequestPayload) => this.post(FIRMS.createRequestedQuestion, data);

  deleteRequestedQuestion = (questionId: number): Promise<any> =>
    this.delete(FIRMS.deleteRequestedQuestion(questionId));

  updateRequestedQuestion = (questionId: number, data: PayloadToUpdateRequestedQuestion): Promise<any> =>
    this.put(FIRMS.updateRequestedQuestion(questionId), data);

  createFirmRequestedDocument = (data: FirmCreateDocRequestPayload) =>
    this.post(FIRMS.createFirmRequestedDocument, data);

  deleteFirmRequestedDocument = (requestedDocumentId: number): Promise<any> =>
    this.delete(FIRMS.deleteFirmRequestedDocument(requestedDocumentId));

  updateFirmRequestedDocument = (requestedDocumentId: number, data: PayloadToUpdateRequestedQuestion): Promise<any> =>
    this.put(FIRMS.updateFirmRequestedDocument(requestedDocumentId), data);

  hasFirmSummary = (
    firmId?: number,
    fundsByMeasurementDate?: { funds_by_measurement_date: ParsedFundByMeasurementDate[] }
  ) => this.post(FIRMS.hasFirmSummary(firmId), fundsByMeasurementDate);

  getFirmAssetSignedUrl = (firmId: number, fileType: string) => this.get(FIRMS.getFirmAssetSignedUrl(firmId, fileType));

  /**
   * @function
   * @name getFirmInvestments
   * @description Get the firm-level schedule of investments. When `fundId` is
   * provided the response matches the fund-level endpoint; when omitted it
   * spans every fund in the firm and converts each company's rows to that
   * company's cap-table currency.
   */
  getFirmInvestments = (
    firmId: number,
    fundId: number | null | undefined,
    date?: string | null,
    checkForExport: boolean = false
  ) => this.get(FIRMS.getFirmInvestments(firmId, fundId, date, checkForExport));

  /**
   * @function
   * @name exportDailyNav
   * @description Download a Daily NAV day as an Excel workbook.
   */
  exportDailyNav = (firmId: number, date?: string | null) =>
    this.downloadFile<Blob>(FIRMS.exportDailyNav(firmId, date));

  exportIntelligenceSummary = (
    firmId: number,
    definitionId: number,
    summaryType: 'firm' | 'valuations',
    payload: IntelligenceExcelExport
  ) =>
    this.getFile<Blob, IntelligenceExcelExport>(
      FIRMS.exportIntelligenceSummary(firmId, definitionId, summaryType),
      payload
    );
}

export default FirmsService;
